import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Header from "../../../components/ui/Header";
import Sidebar from "../../../components/ui/Sidebar";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

/* ---------------- UI Helpers ---------------- */
const ModalShell = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50">
    <div className="absolute inset-0 bg-black/30" onClick={onClose} />
    <div className="absolute inset-0 flex items-center justify-center p-3">
      <div className="w-full max-w-xl bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          <button
            type="button"
            className="p-1 rounded hover:bg-gray-100"
            onClick={onClose}
          >
            <Icon name="X" size={16} />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  </div>
);

const Badge = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${className}`}
  >
    {children}
  </span>
);

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

/* ---------------- Page ---------------- */
function CollectionPriceManagement() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // dropdown data
  const [centers, setCenters] = useState([]);
  const [cities, setCities] = useState([]);

  // filters
  const [centerId, setCenterId] = useState("");
  const [cityId, setCityId] = useState("");
  const [pincode, setPincode] = useState("");
  const [minAmount, setMinAmount] = useState(""); // ✅ NEW filter
  const [isActive, setIsActive] = useState("all"); // all | true | false

  // modals
  const [openCreate, setOpenCreate] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const handleMenuToggle = () => setSidebarOpen((p) => !p);
  const handleSidebarClose = () => setSidebarOpen(false);

  const queryParams = useMemo(() => {
    const params = {};
    if (centerId) params.centerId = centerId;
    if (cityId) params.cityId = cityId;
    if (pincode) params.pincode = pincode.trim();
    if (minAmount !== "" && Number.isFinite(Number(minAmount))) params.minAmount = Number(minAmount); // ✅ NEW
    if (isActive !== "all") params.isActive = isActive;
    return params;
  }, [centerId, cityId, pincode, minAmount, isActive]);

  const fetchCenters = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/centers`, {
        params: { limit: 200 },
        withCredentials: true,
      });
      const data = res.data;
      setCenters(data.centers || data.data || data || []);
    } catch (e) {
      console.warn("Centers fetch failed (ok if not used):", e);
    }
  };

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/cities`, {
        params: { limit: 200 },
        withCredentials: true,
      });
      const data = res.data;
      setCities(data.cities || data.data || data || []);
    } catch (e) {
      console.warn("Cities fetch failed (ok if not used):", e);
    }
  };

  const fetchRows = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get(`${API_BASE}/api/collection-prices`, {
        params: queryParams,
        withCredentials: true,
      });
      const data = res.data;
      setRows(data.collectionPrices || data.data || data || []);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          "Failed to load collection prices."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCenters();
    fetchCities();
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // auto refetch when filters change (small debounce)
  useEffect(() => {
    const t = setTimeout(() => fetchRows(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const handleDelete = async (id) => {
    if (!confirm("Delete this collection price rule?")) return;
    try {
      await axios.delete(`${API_BASE}/api/collection-prices/${id}`, {
        withCredentials: true,
      });
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      alert(e?.response?.data?.error || "Failed to delete.");
    }
  };

  const scopeLabel = (r) => {
    const parts = [];
    if (r.centerId) parts.push(`Center: ${r.center?.name || r.centerId}`);
    if (r.cityId) parts.push(`City: ${r.city?.name || r.cityId}`);
    if (r.pincode) parts.push(`Pincode: ${r.pincode}`);
    if (!parts.length) return "Global Default";
    return parts.join(" • ");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main>
        <div className="p-4">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-1">
                Collection Price
              </h1>
              <p className="text-xs text-gray-600">
                Configure home collection charges by center/city/pincode (with slabs using Min Amount).
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchRows}
                className="text-xs px-3 py-2"
              >
                <Icon name="RefreshCcw" size={14} className="mr-1" />
                Refresh
              </Button>

              <Button
                onClick={() => setOpenCreate(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2"
              >
                <Icon name="Plus" size={14} className="mr-1" />
                Add Rule
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Center
                </label>
                <select
                  value={centerId}
                  onChange={(e) => setCenterId(e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                >
                  <option value="">All</option>
                  {centers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  City
                </label>
                <select
                  value={cityId}
                  onChange={(e) => setCityId(e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                >
                  <option value="">All</option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Pincode
                </label>
                <input
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                  placeholder="e.g. 560001"
                  className="w-full px-2 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                />
              </div>

              {/* ✅ NEW: Min Amount filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Min Amount (₹)
                </label>
                <input
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  placeholder="e.g. 0 or 500"
                  className="w-full px-2 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Active
                </label>
                <select
                  value={isActive}
                  onChange={(e) => setIsActive(e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs"
                >
                  <option value="all">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="flex items-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCenterId("");
                    setCityId("");
                    setPincode("");
                    setMinAmount(""); // ✅ NEW
                    setIsActive("all");
                  }}
                  className="text-xs px-3 py-2 w-full"
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  Rules
                </div>
                <div className="text-xs text-gray-600">
                  {rows.length} rules found
                </div>
              </div>

              {loading && (
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                  Loading...
                </div>
              )}
            </div>

            {error ? (
              <div className="p-4">
                <div className="flex items-start gap-2 text-red-600 text-xs">
                  <Icon name="AlertCircle" size={16} />
                  <div>
                    <div className="font-semibold">Error</div>
                    <div className="text-red-500">{error}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-white border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">
                        Scope
                      </th>
                      {/* ✅ NEW column */}
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">
                        Min Amount
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">
                        Price
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">
                        Status
                      </th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">
                    {rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-6 text-center text-gray-500"
                        >
                          No rules found.
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => {
                        const minA = Number(r.minAmount ?? 0);
                        return (
                          <tr key={r.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="text-gray-900 font-medium">
                                {scopeLabel(r)}
                              </div>
                              <div className="text-gray-500">
                                #{r.id}
                                {r.createdBy?.name
                                  ? ` • by ${r.createdBy.name}`
                                  : ""}
                              </div>
                            </td>

                            {/* ✅ NEW cell */}
                            <td className="px-4 py-3">
                              <div className="font-semibold text-gray-900">
                                ₹{Number.isFinite(minA) ? minA.toFixed(2) : "0.00"}
                              </div>
                              <div className="text-gray-500">
                                Applies when amount ≥ this
                              </div>
                            </td>

                            <td className="px-4 py-3">
                              <div className="font-semibold text-gray-900">
                                ₹{Number(r.price || 0).toFixed(2)}
                              </div>
                            </td>

                            <td className="px-4 py-3">
                              {r.isActive ? (
                                <Badge className="bg-green-50 text-green-700 border-green-200">
                                  Active
                                </Badge>
                              ) : (
                                <Badge className="bg-gray-50 text-gray-700 border-gray-200">
                                  Inactive
                                </Badge>
                              )}
                            </td>

                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs px-3 py-1"
                                  onClick={() => setEditRow(r)}
                                >
                                  <Icon name="Pencil" size={14} className="mr-1" />
                                  Edit
                                </Button>

                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs px-3 py-1 border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => handleDelete(r.id)}
                                >
                                  <Icon name="Trash2" size={14} className="mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modals */}
      {openCreate && (
        <CollectionPriceModal
          title="Add Collection Price Rule"
          centers={centers}
          cities={cities}
          initial={{
            centerId: "",
            cityId: "",
            pincode: "",
            minAmount: "0", // ✅ NEW
            price: "",
            isActive: true,
          }}
          onClose={() => setOpenCreate(false)}
          onSubmit={async (payload) => {
            await axios.post(`${API_BASE}/api/collection-prices`, payload, {
              withCredentials: true,
            });
            setOpenCreate(false);
            await fetchRows();
          }}
        />
      )}

      {editRow && (
        <CollectionPriceModal
          title="Edit Collection Price Rule"
          centers={centers}
          cities={cities}
          initial={{
            centerId: toStr(editRow.centerId),
            cityId: toStr(editRow.cityId),
            pincode: toStr(editRow.pincode),
            minAmount: toStr(editRow.minAmount ?? 0), // ✅ NEW
            price: toStr(editRow.price),
            isActive: !!editRow.isActive,
          }}
          onClose={() => setEditRow(null)}
          onSubmit={async (payload) => {
            await axios.put(
              `${API_BASE}/api/collection-prices/${editRow.id}`,
              payload,
              { withCredentials: true }
            );
            setEditRow(null);
            await fetchRows();
          }}
        />
      )}
    </div>
  );
}

/* ---------------- Modal Form ---------------- */
function CollectionPriceModal({
  title,
  centers,
  cities,
  initial,
  onClose,
  onSubmit,
}) {
  const [centerId, setCenterId] = useState(initial.centerId);
  const [cityId, setCityId] = useState(initial.cityId);
  const [pincode, setPincode] = useState(initial.pincode);
  const [minAmount, setMinAmount] = useState(initial.minAmount ?? "0"); // ✅ NEW
  const [price, setPrice] = useState(initial.price);
  const [isActive, setIsActive] = useState(initial.isActive);

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");

    const priceNum = Number(price);
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      return setErr("Please enter a valid price.");
    }

    const minNum = minAmount === "" ? 0 : Number(minAmount);
    if (!Number.isFinite(minNum) || minNum < 0) {
      return setErr("Please enter a valid Min Amount (>= 0).");
    }

    const payload = {
      centerId: centerId ? Number(centerId) : null,
      cityId: cityId ? Number(cityId) : null,
      pincode: pincode.trim() ? pincode.trim() : null,
      minAmount: minNum, // ✅ NEW
      price: priceNum,
      isActive: !!isActive,
    };

    try {
      setSaving(true);
      await onSubmit(payload);
    } catch (e2) {
      setErr(
        e2?.response?.data?.error ||
          e2?.response?.data?.message ||
          "Failed to save."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title={title} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {err && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {err}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Center (optional)
            </label>
            <select
              value={centerId}
              onChange={(e) => setCenterId(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-md"
            >
              <option value="">None</option>
              {centers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              City (optional)
            </label>
            <select
              value={cityId}
              onChange={(e) => setCityId(e.target.value)}
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-md"
            >
              <option value="">None</option>
              {cities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Pincode (optional)
            </label>
            <input
              value={pincode}
              onChange={(e) => setPincode(e.target.value)}
              placeholder="e.g. 560001"
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-md"
            />
          </div>

          {/* ✅ NEW: Min Amount */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Min Amount (₹)
            </label>
            <input
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              placeholder="e.g. 0 or 500"
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-md"
            />
            <div className="text-[11px] text-gray-500 mt-1">
              This rule applies when order amount ≥ Min Amount.
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Price (₹)
            </label>
            <input
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="e.g. 80"
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-md"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="inline-flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={!!isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Active
            </label>
            <div className="text-[11px] text-gray-500 mt-1">
              Tip: Add one “Global Default” rule (no center/city/pincode) with Min Amount = 0 for fallback.
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

export default CollectionPriceManagement;
